// Mouse, pen and touch input on the canvas.
//   one pointer      pan (3D: rotate; shift+drag or a two-finger drag pans)
//   two pointers     pinch zoom about the fingers' centroid plus pan
//   double tap       zoom in 2x at the tap (touch only; mice have the wheel)
//   wheel            zoom about the cursor
// The canvas has `touch-action: none`, so the browser never turns these into
// page scrolls or page zooms and never cancels the pointer sequence.
import type { App } from '../app.ts';
import type { Capture } from '../ui/capture.ts';
import { zoomAt } from '../render/camera.ts';
import { applyPinch, DOUBLE_TAP_ZOOM, DoubleTap, pinchOf, wheelZoomFactor, type Pinch, type Pt } from './gesture.ts';


export function mountPointer(app: App, cap: Capture): void {
  const canvas = app.renderer.canvas;
  /** Active pointers in press order, with their last canvas-local position. */
  const active = new Map<number, Pt>();
  const pressed = new Map<number, { p: Pt; t: number }>();
  let mode: 'pan' | 'rotate' = 'pan';
  let pinch: Pinch | null = null;
  let markId = -1;              // pointer drawing the "save a region" marquee
  const taps = new DoubleTap();

  const local = (ev: { clientX: number; clientY: number }): Pt => {
    const r = canvas.getBoundingClientRect();
    return { x: ev.clientX - r.left, y: ev.clientY - r.top };
  };
  const firstTwo = (): Pinch | null => {
    if (active.size < 2) return null;
    const it = active.values();
    return pinchOf(it.next().value as Pt, it.next().value as Pt);
  };
  const moved = () => { app.loop.markInteracting(); app.requestDraw(); };

  canvas.addEventListener('contextmenu', (ev) => ev.preventDefault());
  canvas.addEventListener('pointerdown', (ev) => {
    const rightDrag = ev.pointerType === 'mouse' && ev.button === 2;
    if (ev.pointerType === 'mouse' && ev.button !== 0 && !rightDrag) return;   // middle button: leave to the browser
    const p = local(ev);
    canvas.setPointerCapture(ev.pointerId);
    if (cap.picking) {
      if (markId < 0) { markId = ev.pointerId; cap.beginMark(p.x, p.y); }
      return;
    }
    active.set(ev.pointerId, p);
    pressed.set(ev.pointerId, { p, t: ev.timeStamp });
    if (active.size === 1) mode = app.is3d() && app.state.view3d.mode === 'orbit' && !ev.shiftKey && !rightDrag ? 'rotate' : 'pan';
    pinch = firstTwo();           // re-anchor whenever the finger set changes
    if (pinch) taps.reset();
  });

  canvas.addEventListener('pointermove', (ev) => {
    if (ev.pointerId === markId) { const p = local(ev); cap.moveMark(p.x, p.y); return; }
    const prev = active.get(ev.pointerId);
    if (!prev) return;
    const p = local(ev);
    active.set(ev.pointerId, p);
    if (active.size >= 2) {
      const next = firstTwo()!;
      if (pinch) applyPinch(app.cam, pinch, next);
      pinch = next;
    } else if (mode === 'rotate') {
      app.orbit(prev.x, prev.y, p.x, p.y);
    } else {
      app.cam.tx += p.x - prev.x;
      app.cam.ty += p.y - prev.y;
    }
    moved();
  });

  const endPointer = (ev: PointerEvent) => {
    if (ev.pointerId === markId) { markId = -1; const p = local(ev); void cap.endMark(p.x, p.y); return; }
    if (!active.has(ev.pointerId)) return;
    const p = local(ev);
    const down = pressed.get(ev.pointerId);
    const wasPinch = pinch !== null;
    active.delete(ev.pointerId);
    pressed.delete(ev.pointerId);
    pinch = firstTwo();
    if (ev.type === 'pointerup' && ev.pointerType === 'touch' && !wasPinch && active.size === 0 && down
        && taps.up(down.p, down.t, p, ev.timeStamp)) {
      zoomAt(app.cam, p.x, p.y, DOUBLE_TAP_ZOOM);
      moved();
    }
    if (active.size === 0) app.loop.settleSoon(120);
  };
  canvas.addEventListener('pointerup', endPointer);
  canvas.addEventListener('pointercancel', endPointer);

  canvas.addEventListener('wheel', (ev) => {
    ev.preventDefault();
    const p = local(ev);
    zoomAt(app.cam, p.x, p.y, wheelZoomFactor(ev.deltaY, ev.deltaMode));
    moved();
  }, { passive: false });
}
